import { addDays, subDays, isSameDay, isAfter } from 'date-fns';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { IMenuItem } from '../utils/types';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { PhxFocusableItemDirective } from '../phx-focusable-item.directive';
import { KeyboardKeys } from './../../../helpers/KeyboardKeys.enum';
import { ISelectedRange } from '../utils/types';

@Component({
  selector: 'phx-date-picker-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'phx-date-picker-menu',
    tabindex: '0',
    '(focus)': 'onWrapperFocus()',
  },
})
export class PhxDatePickerMenuComponent implements AfterViewInit {
  public selectedItemIndex: number = null;

  @ViewChildren(PhxFocusableItemDirective)
  private focusableItems: QueryList<PhxFocusableItemDirective>;
  private keyManager: FocusKeyManager<PhxFocusableItemDirective>;

  @Input() public readonly menuItems: Array<IMenuItem>;
  @Input() public set selectedRange(value: ISelectedRange) {
    if (value) this.preSelectMenuItem(value.start, value.end);
  };

  @Output() readonly onItemSelection = new EventEmitter<ISelectedRange>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.keyManager = new FocusKeyManager(this.focusableItems).withWrap();
    this.keyManager.setFirstItemActive();
  }

  public onMenuKeydown(event: KeyboardEvent, menuItem: IMenuItem, index: number): void {
    if (event.code === KeyboardKeys.ENTER || event.code === KeyboardKeys.SPACE) {
      event.preventDefault();
      this.selectItem(menuItem, index);
    } else if (event.code !== KeyboardKeys.TAB) {
      event.preventDefault();
      this.keyManager.onKeydown(event);
    }
  }

  public onWrapperFocus(): void {
    this.keyManager.setFirstItemActive();
  }

  public selectItem(menuItem: IMenuItem, index: number): void {
    if (index === this.selectedItemIndex) this.selectedItemIndex = null;
    this.onItemSelection.emit(this.getMenuItemDates(menuItem));
  }

  public clearItemSelection(): void {
    this.selectedItemIndex = null;
    this.cdr.detectChanges();
  }

  private convertValueIntoEndDate(valueInDays: number, startDate: Date): Date {
    if (valueInDays > 0) return addDays(startDate, valueInDays - 1);
    if (valueInDays < 0) return subDays(startDate, (valueInDays + 1) * -1);
    return new Date();
  }

  private getMenuItemDates(menuItem: IMenuItem): ISelectedRange {
    let startDate: Date;
    let endDate: Date;

    if ('startDate' in menuItem && 'endDate' in menuItem) {
      startDate = menuItem.startDate;
      endDate = menuItem.endDate;
    } else if ('startDate' in menuItem && 'valueInDays' in menuItem) {
      startDate = menuItem.startDate;
      endDate = this.convertValueIntoEndDate(menuItem.valueInDays, startDate);
    } else {
      startDate = new Date();
      endDate = this.convertValueIntoEndDate(menuItem.valueInDays, startDate);
    }

    if (isAfter(startDate, endDate)) return { start: endDate, end: startDate };
    return { start: startDate, end: endDate };
  }

  private preSelectMenuItem(startDate: Date, endDate: Date): void {
    const previousIndex: number = this.selectedItemIndex;

    this.menuItems.forEach((menuItem, index) => {
      const { start, end } = this.getMenuItemDates(menuItem);
      if (isSameDay(startDate, start) && isSameDay(endDate, end)) this.selectedItemIndex = index;
    });

    if (this.selectedItemIndex === previousIndex) this.selectedItemIndex = null;
  }
}
